<script lang="ts">
  import { captureMessage, withScope } from '@sentry/sveltekit';

  type Sources = {
    mov: string;
    webm: string;
  };

  export let sources: Sources;
  export let placeholderSrc: string;
  export let alt: string;

  let observer: IntersectionObserver;
  let containerEl: HTMLDivElement;
  let videoEl: HTMLVideoElement;
  let videoReadyState: number;
  let showVideo = false;
  let isInView = false;

  function handleIntersectionStateChange(entries: IntersectionObserverEntry[]) {
    const containerElEntry = entries.find((entry) => entry.target === containerEl);

    if (containerElEntry?.isIntersecting) {
      isInView = true;
      if (videoEl) {
        videoEl.load();
      }
    }
  }

  $: if (containerEl && observer === undefined) {
    observer = new IntersectionObserver(handleIntersectionStateChange, { rootMargin: '100px' });
    observer.observe(containerEl);
  }

  $: if (videoEl && isInView) {
    videoEl.load();
  }

  // If neither of our video formats is supported show video tag with poster fallback.
  // hvc1 codec is not supported on iOS 12.2 - 12.5
  const videoFormatsAreSupported = () =>
    !(
      videoEl?.canPlayType('video/quicktime; codecs="hvc1"') == '' &&
      videoEl?.canPlayType('video/webm') == ''
    );

  // 3 is a state at which there is enough data for current playback position and at least a little
  // bit of time into the future (couple frames).
  // For more details see: https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
  $: if (videoEl && videoReadyState >= 3 && videoFormatsAreSupported()) {
    showVideo = true;

    try {
      videoEl.play();
    } catch (error) {
      withScope((scope) => {
        scope.setContext('Error', { value: error });
        captureMessage('Video playback failed', 'info');
      });
    }
  }
</script>

<div bind:this={containerEl}>
  {#if isInView}
    <video
      bind:this={videoEl}
      bind:readyState={videoReadyState}
      class:hidden={!showVideo}
      tabindex="-1"
      muted
      loop
      playsinline
      preload="none"
    >
      <source src={sources.mov} type={`video/quicktime; codecs="hvc1"`} />
      <source src={sources.webm} type="video/webm" />
    </video>
  {/if}

  <img class:hidden={showVideo} src={placeholderSrc} {alt} />
</div>

<style lang="scss">
  @import 'src/scss/accessibility';

  video.hidden {
    display: none;
  }

  video {
    width: 100%;
    display: block;
  }

  img {
    width: 100%;
    display: none;
  }

  @include prefers-reduce-motion {
    video {
      display: none;
    }

    img {
      display: block;
    }
  }
</style>
